import { useState, type FormEvent } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import {
  useBillingConfig,
  useCreatePelviaCareIntent,
  useFinalizePelviaCare,
  type PelviaCareIntentResponse,
} from '@pelvia/shared/hooks/patient/useBilling'
import { missingDemographicsFrom } from '@pelvia/shared/hooks/patient/useAkute'
import { ApiError } from '@/lib/api'
import DemographicsModal from '@/components/DemographicsModal'

// Cache the Stripe.js loader per publishable key so reopening the checkout
// doesn't reload the script.
const stripePromises = new Map<string, Promise<Stripe | null>>()
function getStripe(pk: string): Promise<Stripe | null> {
  let p = stripePromises.get(pk)
  if (!p) {
    p = loadStripe(pk)
    stripePromises.set(pk, p)
  }
  return p
}

function formatUsd(cents: number): string {
  return `$${(cents / 100).toFixed(cents % 100 === 0 ? 0 : 2)}`
}

// The single tall panel (marketing copy + Stripe Payment Element stacked) was
// taller than the viewport. Splitting it into short steps keeps each screen
// inside the fold: a brief "what you get" review, then just the payment fields,
// then the confirmation. Demographics (when the server needs them) pop as an
// interstitial between review and payment.
type Step = 'review' | 'pay' | 'done'
const STEPS: { key: Step; label: string }[] = [
  { key: 'review', label: 'Review' },
  { key: 'pay', label: 'Payment' },
  { key: 'done', label: 'Done' },
]

function StepIndicator({ current }: { current: Step }) {
  const activeIdx = STEPS.findIndex((s) => s.key === current)
  return (
    <ol className="checkout-steps" aria-label="Checkout progress">
      {STEPS.map((s, i) => (
        <li
          key={s.key}
          className={`checkout-step ${i === activeIdx ? 'is-active' : ''} ${i < activeIdx ? 'is-done' : ''}`}
          aria-current={i === activeIdx ? 'step' : undefined}
        >
          <span className="checkout-step-dot">{i < activeIdx ? '✓' : i + 1}</span>
          <span className="checkout-step-label">{s.label}</span>
        </li>
      ))}
    </ol>
  )
}

/**
 * PelviaCare+ checkout wizard. Creates the $300 PaymentIntent, collects
 * demographics first if the server asks (so we never charge then fail to
 * fulfill), then mounts the Stripe Payment Element.
 *
 * Payment makes the patient a member. Sending the consult note is a *separate*
 * step they do from their dashboard (where they pick the authoring/signing
 * clinician). The success step calls `finalize` to flip membership immediately
 * (and to cover local dev, where the Stripe webhook isn't delivered).
 */
export default function PelviaCareCheckout({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const { data: cfg } = useBillingConfig()
  const createIntent = useCreatePelviaCareIntent()
  const [step, setStep] = useState<Step>('review')
  const [intent, setIntent] = useState<PelviaCareIntentResponse | null>(null)
  const [demographicsOpen, setDemographicsOpen] = useState(false)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [blocked, setBlocked] = useState<string | null>(null)

  const amountCents = intent?.amount_cents ?? cfg?.amount_cents ?? 30000

  // Create the PaymentIntent, then advance to the payment step. If the server
  // needs demographics first, surface that modal and re-run on save. Reuse an
  // already-created intent so going Back to review and forward again doesn't
  // open a second order.
  function start() {
    setBlocked(null)
    if (intent) {
      setStep('pay')
      return
    }
    createIntent.mutate(undefined, {
      onSuccess: (res) => {
        setDemographicsOpen(false)
        setIntent(res)
        setStep('pay')
      },
      onError: (err) => {
        const missing = missingDemographicsFrom(err)
        if (missing) {
          setMissingFields(missing)
          setDemographicsOpen(true)
          return
        }
        if (err instanceof ApiError && err.status === 409) {
          // Already a member (paid/enrolled). Refresh so the homepage card flips
          // to the member view, and keep them on the dashboard — no Integrations
          // detour; the note flow lives here now.
          qc.invalidateQueries({ queryKey: ['billing'] })
          qc.invalidateQueries({ queryKey: ['akute'] })
          setBlocked(
            "You're already a PelviaCare+ member — close this to send your note from your dashboard.",
          )
          return
        }
        setBlocked("We couldn't start checkout. Please try again.")
      },
    })
  }

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel checkout-panel" onClick={(e) => e.stopPropagation()}>
        <StepIndicator current={step} />

        {step === 'review' && (
          <div className="checkout-review">
            <h3>Get your note reviewed &amp; signed</h3>
            <p>
              A board-certified clinician reviews your Pelvia history, signs your
              consult note, and returns your next step within 72 hours.
            </p>
            <ul className="checkout-review-list">
              <li>Clinician review of your full symptom &amp; pattern history</li>
              <li>A signed consult note for your insurance or referral</li>
              <li>Your next step — OB/GYN eval or surgery consult</li>
            </ul>
            <div className="checkout-price-row">
              <span className="checkout-price">{formatUsd(amountCents)}</span>
              <span className="checkout-price-sub">one-time · HIPAA-secure</span>
            </div>

            {blocked && <p className="integration-error">{blocked}</p>}

            <div className="modal-actions">
              <button type="button" className="btn btn-ghost" onClick={onClose}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={start}
                disabled={createIntent.isPending}
              >
                {createIntent.isPending ? 'Preparing…' : 'Continue'}
              </button>
            </div>
          </div>
        )}

        {step === 'pay' && intent && (
          <div className="checkout-pay">
            <h3>Payment</h3>
            <p>
              {formatUsd(amountCents)} one-time. After payment you'll choose your
              clinician and send your note from your dashboard.
            </p>
            <Elements
              stripe={getStripe(intent.publishable_key)}
              options={{ clientSecret: intent.client_secret, appearance: { theme: 'stripe' } }}
            >
              <PaymentForm
                amountCents={amountCents}
                onPaid={() => {
                  // Refresh membership (billing) + note status (akute) so the
                  // homepage card flips to the member view right away.
                  qc.invalidateQueries({ queryKey: ['billing'] })
                  qc.invalidateQueries({ queryKey: ['akute'] })
                  setStep('done')
                }}
                onBack={() => setStep('review')}
              />
            </Elements>
          </div>
        )}

        {step === 'done' && (
          <div className="checkout-success">
            <h4>You're a PelviaCare+ member</h4>
            <p>
              Last step: from your dashboard, choose your clinician and send your
              note. They'll review and sign it, and return your next step within
              72 hours.
            </p>
            <div className="modal-actions">
              <button type="button" className="btn btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}

        {demographicsOpen && (
          <DemographicsModal
            missingFields={missingFields}
            onClose={() => {
              setDemographicsOpen(false)
              onClose()
            }}
            onSaved={start}
          />
        )}
      </div>
    </div>
  )
}

function PaymentForm({
  amountCents,
  onPaid,
  onBack,
}: {
  amountCents: number
  onPaid: () => void
  onBack: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const finalize = useFinalizePelviaCare()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError(null)
    const { error: payErr, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
      confirmParams: { return_url: window.location.origin + window.location.pathname },
    })
    if (payErr) {
      setError(payErr.message || 'Payment failed. Please try again.')
      setSubmitting(false)
      return
    }
    if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Flip the patient to a member now. Best-effort: the Stripe webhook is the
      // durable backstop, so a failure here must not block the success screen.
      try {
        await finalize.mutateAsync(paymentIntent.id)
      } catch {
        /* webhook will catch up */
      }
      onPaid()
      return
    }
    // 'processing'/'requires_action' are driven by Stripe; reflect a neutral state.
    setSubmitting(false)
  }

  return (
    <form onSubmit={handleSubmit} className="checkout-form">
      <PaymentElement />
      {error && <p className="integration-error">{error}</p>}
      <div className="modal-actions">
        <button type="button" className="btn btn-ghost" onClick={onBack} disabled={submitting}>
          Back
        </button>
        <button type="submit" className="btn btn-primary" disabled={!stripe || submitting}>
          {submitting ? 'Processing…' : `Pay ${formatUsd(amountCents)}`}
        </button>
      </div>
    </form>
  )
}
